import { z } from 'zod';
import { addDays, isoWeekday, LocalDate, LocalTime } from './time';

// The events' words and the weekly rule (D12, D32), for the API (B1's tables and generator, B2's
// routes) and the web (B3's lists, B4's form). An event is a training, a race or a social, one-off
// or an occurrence of a weekly series; a member answers yes, no or maybe.

export const EVENT_TYPES = ['training', 'race', 'social'] as const;
export const EventType = z.enum(EVENT_TYPES);
export type EventType = z.infer<typeof EventType>;

/** A cancelled event stays in the lists, said to be cancelled; nothing is deleted by cancelling. */
export const EVENT_STATUSES = ['scheduled', 'cancelled'] as const;
export const EventStatus = z.enum(EVENT_STATUSES);
export type EventStatus = z.infer<typeof EventStatus>;

export const RSVP_ANSWERS = ['yes', 'no', 'maybe'] as const;
export const RsvpAnswer = z.enum(RSVP_ANSWERS);
export type RsvpAnswer = z.infer<typeof RsvpAnswer>;

/**
 * What a series gives each of its occurrences besides the time: its template. A one-off event has
 * the same fields, and so does every occurrence, as columns of its own (D32).
 */
export const TEMPLATE_FIELDS = [
  'type',
  'title',
  'description',
  'place',
  'mapUrl',
  'durationMin',
] as const;
export type TemplateField = (typeof TEMPLATE_FIELDS)[number];

/**
 * What an occurrence of a series can have of its own, changed for it alone (B2's "this one"): its
 * overrides, which a change of the series leaves as they are (D12, D32).
 */
export const OCCURRENCE_FIELDS = [...TEMPLATE_FIELDS, 'startsAt', 'status'] as const;
export type OccurrenceField = (typeof OCCURRENCE_FIELDS)[number];

/** A day of the week, ISO's way: 1 is Monday, 7 is Sunday. */
export const Weekday = z.number().int().min(1).max(7);
export type Weekday = z.infer<typeof Weekday>;

/** The days a series meets: at least one, each once, kept in the week's order. */
export const Weekdays = z
  .array(Weekday)
  .min(1)
  .refine((days) => new Set(days).size === days.length, 'a day twice')
  .transform((days) => [...days].sort((a, b) => a - b));

/**
 * A series' weekly rule: the days, the time on the club's clock (Asia/Nicosia, time.ts), the first
 * date it applies from and the last it runs to (inclusive), or none: open until changed.
 */
export const WeeklyRule = z
  .object({
    weekdays: Weekdays,
    startTime: LocalTime,
    fromDate: LocalDate,
    untilDate: LocalDate.nullable(),
  })
  .refine(
    (rule) => rule.untilDate === null || rule.untilDate >= rule.fromDate,
    'ends before it starts',
  );
export type WeeklyRule = z.infer<typeof WeeklyRule>;

/**
 * How far ahead a series' occurrences are made (D12): from the club's today to the same weekday
 * eight weeks on. The API makes them (api/src/events/series.ts); the month view says where they
 * stop (B3), since no weekly event exists beyond them yet.
 */
export const WEEKS_AHEAD = 8;

/**
 * The dates from `first` to `last`, both included, on which the rule meets, in order: its days,
 * from its first date to its last.
 */
export function ruleDates(
  rule: Pick<WeeklyRule, 'weekdays' | 'fromDate' | 'untilDate'>,
  first: string,
  last: string,
): string[] {
  const dates: string[] = [];
  // ISO dates compare as strings: the later is the greater.
  const start = first > rule.fromDate ? first : rule.fromDate;
  const end = rule.untilDate !== null && rule.untilDate < last ? rule.untilDate : last;
  for (let date = start; date <= end; date = addDays(date, 1)) {
    if (rule.weekdays.includes(isoWeekday(date))) dates.push(date);
  }
  return dates;
}

// The rules of an event's fields (the owner's answer, 2026-10-18; D33), for the API's bodies (B2)
// and the admin's form (B4), which checks them before sending, as the member forms do.

/** The longest title, place, notes and map link an event can have, for the forms' inputs too. */
export const EVENT_TITLE_MAX_LENGTH = 80;
export const EVENT_PLACE_MAX_LENGTH = 100;
export const EVENT_NOTES_MAX_LENGTH = 2000;
export const MAP_URL_MAX_LENGTH = 2000;
/** The longest an event lasts, in minutes: a day. */
export const EVENT_DURATION_MAX_MIN = 24 * 60;

/** What it's called: 1 to 80 characters, trimmed. */
export const EventTitle = z.string().trim().min(1).max(EVENT_TITLE_MAX_LENGTH);

/** A text an event may go without: trimmed, and empty (or null) is none. */
function optionalText(max: number) {
  return z
    .string()
    .trim()
    .max(max)
    .nullable()
    .transform((text) => (text === '' ? null : text));
}

/** Where it meets: up to 100 characters, or none. */
export const EventPlace = optionalText(EVENT_PLACE_MAX_LENGTH);

/** Its notes (the description column): up to 2,000 characters, or none. */
export const EventNotes = optionalText(EVENT_NOTES_MAX_LENGTH);

/**
 * Its map link: any https link (Google Maps, Apple Maps, a Strava route …), up to 2,000
 * characters, kept as the browser reads it (zod normalizes it through `URL`: `href`), or none.
 * Only https: a member taps it, so it can run no script (`javascript:`) and open no page without
 * TLS.
 */
export const MapUrl = z
  .string()
  .trim()
  .max(MAP_URL_MAX_LENGTH)
  .nullable()
  .pipe(
    z.union([
      z.null(),
      z.literal('').transform(() => null),
      z.url({ protocol: /^https$/, normalize: true }).max(MAP_URL_MAX_LENGTH),
    ]),
  );

/** How long it lasts: whole minutes, from 1 to 24 hours. */
export const DurationMin = z.number().int().min(1).max(EVENT_DURATION_MAX_MIN);
